import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, ShieldCheck, Trash2, UserCog } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import { PickerSelect } from "@/components/cam/AssetDrawer";

const ROLE_META = {
  cam_admin:      { label: "CAM Admin",       hint: "Full control of the asset module", scope: "none" },
  category_owner: { label: "Category Owner",  hint: "Manages assets in one category",    scope: "category" },
  lab_incharge:   { label: "Lab In-charge",   hint: "Manages assets in one lab",         scope: "location" },
  maintenance:    { label: "Maintenance",     hint: "Handles repairs & work orders",     scope: "none" },
  store_manager:  { label: "Store Manager",   hint: "Manages the central store",         scope: "none" },
};

export default function Roles() {
  const { categories, tree } = useCam();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ role: "cam_admin", holder_email: "", scope_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await camApi.roles(); setRows(r.roles || []); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const meta = ROLE_META[form.role];
  const scopeOptions = meta?.scope === "category"
    ? categories.map((c) => ({ value: String(c.id), label: c.name }))
    : meta?.scope === "location"
      ? tree.labs.map((l) => ({ value: String(l.id), label: l.name }))
      : [];

  const assign = async () => {
    if (!form.holder_email.trim()) { toast.error("Enter the person's email."); return; }
    if (meta.scope !== "none" && !form.scope_id) { toast.error("Pick a scope for this role."); return; }
    setBusy(true);
    try {
      await camApi.assignRole({
        role: form.role, email: form.holder_email.trim(),
        scope_type: meta.scope, scope_id: meta.scope === "none" ? null : Number(form.scope_id),
      });
      toast.success("Role assigned"); setForm({ role: "cam_admin", holder_email: "", scope_id: "" }); await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this role?")) return;
    try { await camApi.removeRole(id); toast.success("Revoked"); await load(); } catch (e) { toast.error(e.message); }
  };

  const scopeLabel = (r) => {
    if (r.scope_type === "category") return categories.find((c) => c.id === r.scope_id)?.name || `category #${r.scope_id}`;
    if (r.scope_type === "location") return tree.labs.find((l) => l.id === r.scope_id)?.name || `lab #${r.scope_id}`;
    return "—";
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 glass rounded-2xl overflow-hidden self-start">
        <div className="p-4 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cobalt-500" /><div className="overline">Access & delegation</div></div>
        {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          : rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No CAM roles assigned yet. Institute admins always have full access.</div>
            : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b">
                  <th className="px-4 py-2.5">Person</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5 hidden sm:table-cell">Scope</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{r.holder?.name || r.holder?.email || `user #${r.holder_id}`}</div>
                        {r.holder?.email && <div className="text-xs text-muted-foreground">{r.holder.email}</div>}
                      </td>
                      <td className="px-4 py-2.5">{ROLE_META[r.role]?.label || r.role}</td>
                      <td className="px-4 py-2.5 hidden sm:table-cell text-muted-foreground">{scopeLabel(r)}</td>
                      <td className="px-4 py-2.5 text-right"><button onClick={() => revoke(r.id)} className="text-muted-foreground hover:text-red-500 p-1"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </div>

      <div className="glass rounded-2xl p-4 self-start">
        <div className="flex items-center gap-2 mb-3"><UserCog className="h-4 w-4 text-green-600" /><div className="overline">Assign a role</div></div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Role</Label>
            <PickerSelect value={form.role} onChange={(v) => setForm({ ...form, role: v, scope_id: "" })} options={Object.entries(ROLE_META).map(([k, v]) => ({ value: k, label: v.label }))} className="mt-1" />
            <p className="text-[11px] text-muted-foreground mt-1">{meta?.hint}</p>
          </div>
          <div><Label className="text-xs">Person's email</Label><Input className="mt-1" placeholder="name@institute.edu" value={form.holder_email} onChange={(e) => setForm({ ...form, holder_email: e.target.value })} /></div>
          {meta?.scope !== "none" && (
            <div>
              <Label className="text-xs">{meta.scope === "category" ? "Category" : "Lab"}</Label>
              <PickerSelect placeholder="Select scope" value={form.scope_id} onChange={(v) => setForm({ ...form, scope_id: v })} options={scopeOptions} className="mt-1" />
            </div>
          )}
          <Button className="w-full bg-cobalt-500 hover:bg-cobalt-600" onClick={assign} disabled={busy}><Plus className="h-4 w-4 mr-1" />{busy ? "Assigning…" : "Assign role"}</Button>
        </div>
      </div>
    </div>
  );
}
